import { getAnomalyMeterIds, getFlowMeterIds } from "./mizuAi";

// ---------------------------------------------------------------------------
// The dashboard's alert counts - one definition per alert type, shared by
// every view that shows one: Mizu AI's Live Status, its alert cards and the
// header badge, and Digital Twin's Alert Center, its drawers, Meter Estate's
// Critical and the Historical view, which are sent these very numbers
// (LegacyFrame, nd-alert-counts) rather than counting for themselves.
//
// Every count is DISTINCT METERS: a meter leaking on two registers, or flagged
// in two anomaly categories, is one meter (a card's list may still show a row
// per register). The windows are the APIs', as their own pages show them:
//
//   leak      apis/get_water_leak, interval "Past Day" - apis/flow_health
//             reports the same set. Meters with a leak active at any time in
//             the last completed calendar day (00:00-24:00 Central, the day
//             before the latest flow data). Operational meters, the Leak Flow
//             page's scope.
//   anomaly   apis/anoamaly_detection, time_range "past_day", every category.
//             Meters flagged anomalous in the day counted back from the latest
//             anomaly data: for GPMD that day and the latest date itself, for
//             other districts the day before it (the API's end bound differs
//             by district - documents/Dashboard Alert Counts.md).
//   backflow  apis/flow_health - the backend scheduler's latest flow-health
//             scan, as the Flow Health page shows it. Meters with backflow in
//             the last 30 days.
//   noflow    apis/flow_health, the same scan. Meters that have reported no
//             flow since the date in their message (the scheduler's
//             threshold).
//   flagged   Distinct meters with a leak, an anomaly or backflow - Digital
//             Twin's Critical. Mizu AI's Hotspots shows the ones the map can
//             plot as its flagged sites, and the rest as "not on map"
//             (mizuAi.js flaggedOnMap).
//
// District: the selected one. Every call carries it as the District header,
// and Digital Twin is sent the same payloads (nd-feeds) and these counts, so
// both views count one district at one moment.
// Refresh (useDashboardFeeds): on load, on a district change, every 15
// minutes, when the backend has loaded new data, and on request. The time
// shown is when the data was fetched - the oldest of the counts' feeds' last
// successful fetches - not the clock. A count is null until its feed first
// answers (shown "--", never 0) and stays null, with the reason, when it never
// does; a later refresh that fails keeps the count, marked stale.
// ---------------------------------------------------------------------------

export const ALERT_COUNT_DEFINITIONS = {
  leak: { label: "Leaks", text: "distinct meters with a leak active in the last completed day (apis/get_water_leak, Past Day)" },
  anomaly: { label: "Anomalies", text: "distinct meters flagged anomalous in the past day, counted back from the latest anomaly data (apis/anoamaly_detection, past_day)" },
  backflow: { label: "Backflow", text: "distinct meters with backflow in the last 30 days, from the latest flow-health scan (apis/flow_health)" },
  noflow: { label: "No recent flow", text: "distinct meters with no recent flow, from the latest flow-health scan (apis/flow_health)" },
  flagged: { label: "Flagged / Critical", text: "distinct meters with a leak, an anomaly or backflow" },
};

// Each count's window in a few words - the line under the counts - and the
// definitions in full, for its tooltip. Digital Twin's Alert Center shows
// the same two (nd-alert-counts).
export const ALERT_COUNT_WINDOWS = "Leaks: last full day · anomalies: past day of anomaly data · backflow (30 days) & no flow: latest scan";
export const ALERT_COUNT_TOOLTIP = Object.values(ALERT_COUNT_DEFINITIONS)
  .map(({ label, text }) => `${label}: ${text}.`)
  .join("\n");

// How often the feeds are kept current (useDashboardFeeds runs these timers):
// the alert feeds on a timer, and the backend scheduler's loads checked so new
// data is fetched as soon as it lands.
export const ALERT_REFRESH_MINUTES = 15;
export const SCHEDULER_CHECK_MINUTES = 5;

const clock = (time) => new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// "updated 3:42 PM" - when the counts' data was fetched - or "" before then.
export const asOfText = (counts) => (counts?.asOf ? `updated ${clock(counts.asOf)}` : "");

// Under the counts' line, after the definitions: when each feed was fetched,
// how the counts stay current, what the backend last loaded, and any refresh
// that failed.
export function freshnessText(counts) {
  if (!counts) return "";
  const lines = [];
  const fetched = [["leak", "leaks"], ["flowHealth", "flow health"], ["anomaly", "anomalies"]]
    .filter(([key]) => counts.fetchedAt?.[key])
    .map(([key, label]) => `${label} ${clock(counts.fetchedAt[key])}`);
  if (fetched.length) lines.push(`Fetched: ${fetched.join(", ")}.`);
  lines.push(`Refreshed every ${ALERT_REFRESH_MINUTES} minutes, as soon as the backend has loaded new data (checked every ${SCHEDULER_CHECK_MINUTES} minutes), and with the refresh button.`);
  const loads = (counts.backend?.tasks || []).map((task) => `${task.name} ${new Date(task.endTime).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`);
  if (loads.length) lines.push(`Backend last loaded: ${loads.join(", ")}. Backflow and no-flow lists come from its cached flow-health scan (kept up to 24 hours).`);
  counts.stale.forEach(({ label, error, fetchedAt }) => {
    lines.push(`${label}: the last refresh failed (${error}) - showing data fetched ${clock(fetchedAt)}.`);
  });
  return lines.join("\n");
}

// One count: its meters (each once), how many, and whether it is in yet -
// `stale` when its latest refresh failed and these are its last good numbers.
function entry(status, ids, error, fetchedAt) {
  const ready = status === "ready";
  const meters = ready ? [...new Set(ids.map(String))] : [];
  return {
    status,
    count: ready ? meters.length : null,
    ids: meters,
    error: error || null,
    stale: ready && !!error,
    fetchedAt: fetchedAt || null,
  };
}

const STALE_LABELS = { leak: "Leaks", flowHealth: "Backflow and no recent flow", anomaly: "Anomalies" };

/**
 * The counts from the dashboard's feeds (useDashboardFeeds): `feeds`, the
 * `errors` of the feeds whose latest fetch failed, `fetchedAt` - each feed's
 * last successful fetch - and, for display, whether a refresh is under way
 * and the backend scheduler's latest loads. flowHealth carries the live leaks
 * too, so it waits for both.
 */
export function alertCounts(feeds = {}, errors = {}, fetchedAt = {}, { refreshing = false, backend = null } = {}) {
  const flow = feeds.flowHealth;
  // In once its feed has answered; failed when it never has (it stands in
  // empty, so its count is unknown, not 0); loading before either.
  const status = (inHand, key) => (inHand && fetchedAt[key] ? "ready" : errors[key] ? "failed" : "loading");
  const flowEntry = (key, ids) => entry(status(!!flow, key), ids, errors[key], fetchedAt[key]);
  const leak = flowEntry("leak", getFlowMeterIds(flow, "leak"));
  const backflow = flowEntry("flowHealth", getFlowMeterIds(flow, "backflow"));
  const noflow = flowEntry("flowHealth", getFlowMeterIds(flow, "no_recent_flow"));
  const anomaly = entry(status(!!feeds.anomaly, "anomaly"), getAnomalyMeterIds(feeds.anomaly), errors.anomaly, fetchedAt.anomaly);

  const parts = [leak, anomaly, backflow];
  const flagged = parts.every((part) => part.status === "ready")
    ? entry("ready", [...new Set(parts.flatMap((part) => part.ids))])
    : entry(parts.some((part) => part.status === "failed") ? "failed" : "loading", []);

  // The counts are as fresh as their oldest feed: the earliest of their last
  // successful fetches, once none is still loading.
  const keys = ["leak", "flowHealth", "anomaly"];
  const fetched = keys.map((key) => fetchedAt[key]).filter(Boolean);
  const settled = [leak, anomaly, backflow, noflow].every((part) => part.status !== "loading");
  const stale = keys
    .filter((key) => fetchedAt[key] && errors[key])
    .map((key) => ({ key, label: STALE_LABELS[key], error: errors[key], fetchedAt: fetchedAt[key] }));
  return {
    leak,
    anomaly,
    backflow,
    noflow,
    flagged,
    asOf: settled && fetched.length ? Math.min(...fetched) : null,
    fetchedAt: Object.fromEntries(keys.map((key) => [key, fetchedAt[key] || null])),
    stale,
    refreshing,
    backend,
  };
}

// A count for display: the number, or "--" while loading or after a failure.
export const countText = (part) => (part?.count == null ? "--" : part.count.toLocaleString());
